import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Mudanca } from '@/types/mudanca';
import { useClientFilter } from './useClientFilter';

export const useMudancas = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  const { data: mudancas = [], isLoading, error } = useQuery({
    queryKey: ['mudancas', currentClientId, showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('mudancas' as any)
        .select('*');

      // Aplicar filtro de cliente se necessário
      if (currentClientId && !showAllClients) {
        query = query.eq('client_id', currentClientId);
      }

      const { data, error } = await query.order('criado_em', { ascending: false });

      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !clientLoading,
  });

  const createMudanca = useMutation({
    mutationFn: async (mudanca: Partial<Mudanca>) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Buscar informações do tipo de mudança para verificar aprovação
      let requerAprovacao = false;
      let statusInicial = 'proposta';

      if (mudanca.tipo_mudanca) {
        const { data: tipoData } = await supabase
          .from('mudanca_tipos' as any)
          .select('requer_aprovacao, aprovacao_automatica')
          .eq('codigo', mudanca.tipo_mudanca)
          .single();

        const typeCheckedData = tipoData as Record<string, any> | null;
        
        if (typeCheckedData && 'requer_aprovacao' in typeCheckedData) {
          requerAprovacao = typeCheckedData.requer_aprovacao;
          
          // Se for tipo padrão (aprovação automática), status = aprovada
          if (typeCheckedData.aprovacao_automatica) {
            statusInicial = 'aprovada';
          } else if (requerAprovacao) {
            // Se requer aprovação manual, status = em_avaliacao
            statusInicial = 'em_avaliacao';
          }
        }
      }
      
      // Criar a mudança
      const { data, error } = await supabase
        .from('mudancas' as any)
        .insert({
          ...mudanca,
          criado_por: user.user?.id,
          solicitante_id: user.user?.id,
          status: statusInicial,
          aprovacao_necessaria: requerAprovacao,
        })
        .select()
        .single();

      if (error) throw error;

      const mudancaData = data as any;

      // Se requer aprovação e tem aprovador definido, criar registro de aprovação
      if (requerAprovacao && mudanca.aprovador_id) {
        await supabase
          .from('mudanca_aprovacoes' as any)
          .insert({
            mudanca_id: mudancaData.id,
            aprovador_id: mudanca.aprovador_id,
            status_aprovacao: 'pendente',
            nivel_aprovacao: 1,
          });
      }

      // Se aprovação automática, criar workflow imediatamente
      if (statusInicial === 'aprovada') {
        await supabase.from('mudanca_workflow' as any).insert([
          { mudanca_id: mudancaData.id, etapa: 'criacao', status: 'concluida', ordem: 1 },
          { mudanca_id: mudancaData.id, etapa: 'avaliacao', status: 'concluida', ordem: 2 },
          { mudanca_id: mudancaData.id, etapa: 'aprovacao', status: 'concluida', ordem: 3 },
          { mudanca_id: mudancaData.id, etapa: 'execucao', status: 'pendente', ordem: 4 },
          { mudanca_id: mudancaData.id, etapa: 'validacao', status: 'pendente', ordem: 5 },
          { mudanca_id: mudancaData.id, etapa: 'encerramento', status: 'pendente', ordem: 6 },
        ]);
      }

      return mudancaData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudancas'] });
      toast({
        title: "Sucesso",
        description: "Mudança criada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao criar mudança:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar mudança. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const updateMudanca = useMutation({
    mutationFn: async ({ id, ...mudanca }: Partial<Mudanca> & { id: string }) => {
      const { data: user } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('mudancas' as any)
        .update({
          ...mudanca,
          atualizado_por: user.user?.id,
          atualizado_em: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudancas'] });
      toast({
        title: "Sucesso",
        description: "Mudança atualizada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao atualizar mudança:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar mudança. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteMudanca = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('mudancas' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudancas'] });
      toast({
        title: "Sucesso",
        description: "Mudança deletada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao deletar mudança:', error);
      toast({
        title: "Erro",
        description: "Erro ao deletar mudança. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    mudancas,
    isLoading,
    error,
    createMudanca,
    updateMudanca,
    deleteMudanca,
  };
};
